import type { EarthingResponse, EarthingWarning } from "../services/earthing";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = EarthingWarning["code"];

// The group values are the shared ones of statusTone.GROUP_TONE, so study.css holds a rule
// and a tone for each: the computed failures under "not-met", the evidence the engineer owes
// and the links to look at under "review-evidence", the stated items under "not-evaluated".
type WarningGroup = "not-met" | "review-evidence" | "not-evaluated";

interface EarthingWarningPanelProps {
  result: EarthingResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  PROTECTIVE_CONDUCTOR_AREA_INSUFFICIENT: "Protective conductor area below the requirement",
  ELECTRODE_RESISTANCE_ABOVE_TARGET: "Electrode resistance above the target",
  FAULT_CURRENT_NOT_ESTABLISHED: "Fault current not established",
  FAULT_RUN_NOT_MAXIMUM_EARTH_FAULT: "Linked fault run is not the maximum earth fault",
  FAULT_RUN_BUS_MISMATCH: "Linked fault run at another bus",
  DISCONNECTION_TIME_NOT_ESTABLISHED: "Disconnection time not established",
  DISCONNECTION_TIME_ABOVE_ADIABATIC_LIMIT: "Disconnection time above 5 s (formula's limit)",
  DISCONNECTION_ROW_NOT_ASSURED: "Linked row's disconnection not assured",
  DISCONNECTION_ROW_NOT_COMPARED: "Linked row's disconnection not compared",
  DISCONNECTION_RUN_BUS_MISMATCH: "Linked earth-fault loop run at another bus",
  K_FACTOR_NOT_ESTABLISHED: "Factor k not established",
  K_TABLE_CASE_NOT_COVERED: "Table case not covered by the page",
  PROTECTIVE_CONDUCTOR_AREA_NOT_ESTABLISHED: "Protective conductor area not established",
  CONDUCTOR_MATERIAL_NOT_ESTABLISHED: "Conductor material not established",
  SOIL_RESISTIVITY_NOT_ESTABLISHED: "Soil resistivity not established",
  SOIL_TEST_RECORD_NOT_DECLARED: "Soil test record not declared",
  TARGET_RESISTANCE_NOT_ESTABLISHED: "Target resistance not established",
  PARALLEL_FACTOR_NOT_ESTABLISHED: "Utilisation factor not established",
  FAULT_CURRENT_SOURCE_NOT_DECLARED: "Fault current source not declared",
  LET_THROUGH_ENERGY_SOURCE_NOT_DECLARED: "Let-through energy source not declared",
  DISCONNECTION_TIME_SOURCE_NOT_DECLARED: "Disconnection time source not declared",
  K_FACTOR_SOURCE_NOT_DECLARED: "k source not declared",
  PROTECTIVE_CONDUCTOR_AREA_SOURCE_NOT_DECLARED: "Conductor area source not declared",
  SEASONAL_FACTOR_SOURCE_NOT_DECLARED: "Seasonal factor source not declared",
  PARALLEL_FACTOR_SOURCE_NOT_DECLARED: "Utilisation factor source not declared",
  TARGET_RESISTANCE_SOURCE_NOT_DECLARED: "Target resistance source not declared",
  ELECTRODE_DIMENSION_SOURCE_NOT_DECLARED: "Electrode dimension source not declared",
  SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED: "System voltage source not declared",
  DECLARED_VALUE_GOVERNS: "Declared value governs over the link or table",
  FAULT_CURRENT_DECLARED: "Fault current declared (not from a stored run)",
  FAULT_CURRENT_FROM_DISCONNECTION_ROW: "Fault current from the linked earth-fault loop row",
  LET_THROUGH_ENERGY_DECLARED: "Let-through energy declared",
  DISCONNECTION_TIME_FROM_LINKED_ROW: "Disconnection time from the linked row",
  T_IS_MAXIMUM_PERMITTED_TIME: "Conservative check: t is the maximum permitted time",
  K_FROM_REGISTER_TABLE: "k from the held table",
  K_DECLARED_NOT_VERIFIED: "k declared, not verified",
  STANDARD_SIZE_NOT_SELECTED: "Standard size not selected",
  PARALLEL_SPACING_NOT_CHECKED: "Electrode spacing not checked",
  SEASONAL_FACTOR_APPLIED: "Seasonal factor applied",
  SEASONAL_FACTOR_NOT_DECLARED: "No seasonal factor declared",
  ARRANGEMENT_IDEAL_PARALLEL: "Groups combined in ideal parallel",
  UNIFORM_SOIL_ASSUMED: "Uniform soil assumed",
  STEP_TOUCH_VOLTAGE_NOT_CHECKED: "Step and touch voltages not checked",
  LIGHTNING_EARTHING_NOT_CHECKED: "Lightning earthing not checked",
  EARTH_LOOP_BONDING_CONTINUITY_NOT_CHECKED: "Loop and bonding continuity not checked",
  CORROSION_ALLOWANCE_NOT_APPLIED: "Corrosion allowance not applied",
  ELECTRODE_CURRENT_LOADING_NOT_CHECKED: "Electrode current loading not checked",
  MINIMUM_DIMENSIONS_NOT_CHECKED: "Minimum dimensions not checked",
  SELECTION_TABLE_NOT_APPLIED: "Selection table not applied",
  BONDING_CONDUCTORS_NOT_SIZED: "Bonding conductors not sized",
  JOINT_TEMPERATURE_NOT_CHECKED: "Joint temperatures not checked",
};

// Where each code is listed: the failed conditions first, then the evidence the engineer owes
// and the links to look at, then what the sheet states (the F-1 lesson of IR-005: the
// not-checked items are always listed).
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  PROTECTIVE_CONDUCTOR_AREA_INSUFFICIENT: "not-met",
  ELECTRODE_RESISTANCE_ABOVE_TARGET: "not-met",
  FAULT_CURRENT_NOT_ESTABLISHED: "review-evidence",
  FAULT_RUN_NOT_MAXIMUM_EARTH_FAULT: "review-evidence",
  FAULT_RUN_BUS_MISMATCH: "review-evidence",
  DISCONNECTION_TIME_NOT_ESTABLISHED: "review-evidence",
  DISCONNECTION_TIME_ABOVE_ADIABATIC_LIMIT: "review-evidence",
  DISCONNECTION_ROW_NOT_ASSURED: "review-evidence",
  DISCONNECTION_ROW_NOT_COMPARED: "review-evidence",
  DISCONNECTION_RUN_BUS_MISMATCH: "review-evidence",
  K_FACTOR_NOT_ESTABLISHED: "review-evidence",
  K_TABLE_CASE_NOT_COVERED: "review-evidence",
  PROTECTIVE_CONDUCTOR_AREA_NOT_ESTABLISHED: "review-evidence",
  CONDUCTOR_MATERIAL_NOT_ESTABLISHED: "review-evidence",
  SOIL_RESISTIVITY_NOT_ESTABLISHED: "review-evidence",
  SOIL_TEST_RECORD_NOT_DECLARED: "review-evidence",
  TARGET_RESISTANCE_NOT_ESTABLISHED: "review-evidence",
  PARALLEL_FACTOR_NOT_ESTABLISHED: "review-evidence",
  FAULT_CURRENT_SOURCE_NOT_DECLARED: "review-evidence",
  LET_THROUGH_ENERGY_SOURCE_NOT_DECLARED: "review-evidence",
  DISCONNECTION_TIME_SOURCE_NOT_DECLARED: "review-evidence",
  K_FACTOR_SOURCE_NOT_DECLARED: "review-evidence",
  PROTECTIVE_CONDUCTOR_AREA_SOURCE_NOT_DECLARED: "review-evidence",
  SEASONAL_FACTOR_SOURCE_NOT_DECLARED: "review-evidence",
  PARALLEL_FACTOR_SOURCE_NOT_DECLARED: "review-evidence",
  TARGET_RESISTANCE_SOURCE_NOT_DECLARED: "review-evidence",
  ELECTRODE_DIMENSION_SOURCE_NOT_DECLARED: "review-evidence",
  SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED: "review-evidence",
  DECLARED_VALUE_GOVERNS: "not-evaluated",
  FAULT_CURRENT_DECLARED: "not-evaluated",
  FAULT_CURRENT_FROM_DISCONNECTION_ROW: "not-evaluated",
  LET_THROUGH_ENERGY_DECLARED: "not-evaluated",
  DISCONNECTION_TIME_FROM_LINKED_ROW: "not-evaluated",
  T_IS_MAXIMUM_PERMITTED_TIME: "not-evaluated",
  K_FROM_REGISTER_TABLE: "not-evaluated",
  K_DECLARED_NOT_VERIFIED: "not-evaluated",
  STANDARD_SIZE_NOT_SELECTED: "not-evaluated",
  PARALLEL_SPACING_NOT_CHECKED: "not-evaluated",
  SEASONAL_FACTOR_APPLIED: "not-evaluated",
  SEASONAL_FACTOR_NOT_DECLARED: "not-evaluated",
  ARRANGEMENT_IDEAL_PARALLEL: "not-evaluated",
  UNIFORM_SOIL_ASSUMED: "not-evaluated",
  STEP_TOUCH_VOLTAGE_NOT_CHECKED: "not-evaluated",
  LIGHTNING_EARTHING_NOT_CHECKED: "not-evaluated",
  EARTH_LOOP_BONDING_CONTINUITY_NOT_CHECKED: "not-evaluated",
  CORROSION_ALLOWANCE_NOT_APPLIED: "not-evaluated",
  ELECTRODE_CURRENT_LOADING_NOT_CHECKED: "not-evaluated",
  MINIMUM_DIMENSIONS_NOT_CHECKED: "not-evaluated",
  SELECTION_TABLE_NOT_APPLIED: "not-evaluated",
  BONDING_CONDUCTORS_NOT_SIZED: "not-evaluated",
  JOINT_TEMPERATURE_NOT_CHECKED: "not-evaluated",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Earthing check not met" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "not-evaluated", title: "Stated on the sheet (not checked here)" },
];

type ListedWarning = {
  key: string;
  code: WarningCode;
  message: string;
  prefix: string;
};

/** The study's warnings followed by every conductor row's and every electrode's, in the engine's order, rows named. */
export function listWarnings(result: EarthingResponse): ListedWarning[] {
  const study = result.warnings.map((warning, index) => ({
    key: `study-${warning.code}-${index}`,
    code: warning.code,
    message: warning.message,
    prefix: "",
  }));
  const conductors = result.conductors.flatMap((row) =>
    row.warnings.map((warning, index) => ({
      key: `${row.code}-${warning.code}-${index}`,
      code: warning.code,
      message: warning.message,
      prefix: `Conductor ${row.code}: `,
    })),
  );
  const electrodes = result.electrodes.flatMap((item) =>
    item.warnings.map((warning, index) => ({
      key: `${item.code}-${warning.code}-${index}`,
      code: warning.code,
      message: warning.message,
      prefix: `Electrode ${item.code}: `,
    })),
  );
  return [...study, ...conductors, ...electrodes];
}

export function EarthingWarningPanel({ result }: EarthingWarningPanelProps) {
  const warnings = listWarnings(result);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            const members = warnings.filter((warning) => WARNING_CODE_GROUPS[warning.code] === group);
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>
                        {warning.prefix}
                        {WARNING_CODE_LABELS[warning.code]}
                      </strong>
                      {": "}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default EarthingWarningPanel;
